import {z} from 'zod'
import {Divider} from '@/components/ui/divider/Divider'
import {useTranslation} from '@/translations/i18n'
import {FormSection} from '@/components/commons/form-section/FormSection'
import {Controller, useFormContext} from 'react-hook-form'
import {closeProjectReportToSection, periodicityHypothesisMapToLabel} from '../../utils'
import {CloseProjectReportsSchema, MedicalReportFieldsSchema} from '../../formSchemas'
import {StyledReopeningNextCycleSectionWrapper} from '../start-continuous-project-sections/style'
import {InputSelectMonthYear} from '@/components/commons/input-select-month-year/InputSelectMonthYear'
import {MedicalReportSectionTitle} from '../medical-report-section-title/MedicalReportSectionTitle'
import {MedicalReportTextAreaSections} from '../medical-report-text-area-sections/MedicalReportTextAreaSections'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Label} from '@/components/ui/label/Label'
import {Grid} from '@/components/ui/grid-atoms/GridAtoms'
import {ObjectKeys} from '@/utilities/helpers'
import {FormFieldBox} from '@/components/ui/form-atoms/FormAtoms'
import {RadioField} from '@/components/commons/radio-field/RadioField'
import {InputHelpText} from '@/components/ui/input-help-text/InputHelpText'

const CloseProjectAdditionalInfoSchema = MedicalReportFieldsSchema.pick({
    reopeningNextCycle: true,
    cyclePeriodicityHypothesis: true
})
type CloseProjectAdditionalInfoSchema = z.infer<typeof CloseProjectAdditionalInfoSchema>

export const CloseCyclicalProjectSections = () => {
    return (
        <>
            <ReportSections />
            <Divider direction="horizontal" />
            <AdditionalInfoSection />
        </>
    )
}

const ReportSections = () => {
    const {t} = useTranslation()
    return (
        <>
            <MedicalReportSectionTitle>{t('commons:report', {count: 1})}</MedicalReportSectionTitle>
            <MedicalReportTextAreaSections
                sectionsMap={closeProjectReportToSection}
                schema={CloseProjectReportsSchema}
            />
        </>
    )
}

const AdditionalInfoSection = () => {
    const {t} = useTranslation()
    const form = useFormContext<CloseProjectAdditionalInfoSchema>()
    return (
        <>
            <MedicalReportSectionTitle>{t('commons:additional_info')}</MedicalReportSectionTitle>
            <FormSection
                title={t('medical_records:reopen_next_cycle')}
                description={t('medical_records:reopen_next_cycle_description')}
            >
                <Flexbox direction="column" gap={6} fullWidth>
                    <StyledReopeningNextCycleSectionWrapper direction="column" gap={6} fullWidth>
                        <Controller
                            control={form.control}
                            name="reopeningNextCycle"
                            render={({field, fieldState}) => (
                                <InputSelectMonthYear
                                    label={t('commons:month_year_label')}
                                    onChange={field.onChange}
                                    value={field.value || null}
                                    errorMessage={fieldState.error?.message}
                                />
                            )}
                        />
                    </StyledReopeningNextCycleSectionWrapper>

                    <Controller
                        control={form.control}
                        name="cyclePeriodicityHypothesis"
                        render={({field, fieldState}) => (
                            <Flexbox direction="column" gap={1.5} fullWidth>
                                <Label>{t('medical_records:cycles_number')}</Label>
                                <Flexbox fullWidth direction="column" gap={1.5}>
                                    <Grid columns={3} gap={4} fullWidth>
                                        {ObjectKeys(periodicityHypothesisMapToLabel).map(hypothesisKey => (
                                            <FormFieldBox
                                                key={hypothesisKey}
                                                spanColumn={1}
                                                isSelected={field.value == hypothesisKey}
                                                hasError={!!fieldState.error?.message}
                                            >
                                                <RadioField
                                                    label={t(periodicityHypothesisMapToLabel[hypothesisKey])}
                                                    name={'cyclePeriodicityHypothesis'}
                                                    checked={field.value == hypothesisKey}
                                                    onChange={field.onChange}
                                                    value={hypothesisKey}
                                                />
                                            </FormFieldBox>
                                        ))}
                                    </Grid>
                                    {fieldState.error?.message && <InputHelpText error={fieldState.error?.message} />}
                                </Flexbox>
                            </Flexbox>
                        )}
                    />
                </Flexbox>
            </FormSection>
        </>
    )
}
